import type { RoleplayAttempt, RoleplayDebrief } from '@wundo/shared/roleplay.ts';

/** How an attempt compares with the one before it. */
export type AttemptComparison = 'first' | 'better' | 'lower' | 'equal';

export interface AttemptTrend {
  /** Mean of the attempt's objective scores, or null when it has none. */
  average: number | null;
  comparison: AttemptComparison;
}

/*
 * Averages are shown to one decimal, so they are compared to one decimal too.
 * Rounding only at the end would let 3.04 read "3.0 — beter dan 3.0".
 */
function round(score: number): number {
  return Math.round(score * 10) / 10;
}

/**
 * How well one attempt went, over its objectives.
 *
 * Null rather than zero when there is nothing to average: an attempt the model
 * scored on no objective has no average, and 0.0 would read as the worst
 * possible one.
 */
export function averageScore(debrief: RoleplayDebrief): number | null {
  if (debrief.ratings.length === 0) return null;

  const total = debrief.ratings.reduce((sum, rating) => sum + rating.score, 0);
  return round(total / debrief.ratings.length);
}

/**
 * The nearest attempt before this one that has an average. The list is newest
 * first, so that is further down it. One the user did not take part in was
 * never scored, and comparing against it would invent a drop.
 */
export function previousScoredAttempt(
  attempts: RoleplayAttempt[],
  index: number,
): RoleplayAttempt | undefined {
  return attempts
    .slice(index + 1)
    .find(
      (attempt) => attempt.debrief !== null && averageScore(attempt.debrief) !== null,
    );
}

/**
 * One attempt against the one before it, out of the list the feedback shows.
 * Attempts without a debrief are not in this list: comparing against a blank
 * would invent a drop.
 */
export function attemptTrend(attempts: RoleplayAttempt[], index: number): AttemptTrend {
  const debrief = attempts[index]?.debrief ?? null;
  const average = debrief === null ? null : averageScore(debrief);

  const previous = previousScoredAttempt(attempts, index)?.debrief ?? null;
  const previousAverage = previous === null ? null : averageScore(previous);

  if (average === null || previousAverage === null) {
    return { average, comparison: 'first' };
  }

  if (average > previousAverage) return { average, comparison: 'better' };
  if (average < previousAverage) return { average, comparison: 'lower' };
  return { average, comparison: 'equal' };
}
